import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ChevronRight, BookOpen, GraduationCap, FileText, FilePlus2, HelpCircle, CheckSquare, BookMarked, Sparkles, Layers, Image, Video } from 'lucide-react';
import { Card } from '../components/ui/card';
import { API } from '../context/AuthContext';

/* Unified browse page:
   /teacher/browse             → Class picker
   /teacher/browse/:cls        → Subject picker
   /teacher/browse/:cls/:sub   → Topic picker
   /teacher/browse/:cls/:sub/:topic → Resource types + resources
*/
const RESOURCE_ICONS = {
  notes: { label: 'ନୋଟ୍ସ', en: 'Notes', icon: FileText, color: 'from-emerald-500 to-teal-600' },
  lesson_plan: { label: 'ପାଠ ଯୋଜନା', en: 'Lesson Plan', icon: BookOpen, color: 'from-purple-500 to-pink-600' },
  worksheet: { label: 'ୱାର୍କସିଟ୍', en: 'Worksheet', icon: FilePlus2, color: 'from-orange-500 to-red-600' },
  question_bank: { label: 'ପ୍ରଶ୍ନ ପତ୍ର', en: 'Question Bank', icon: HelpCircle, color: 'from-blue-500 to-indigo-600' },
  assessment: { label: 'ମୂଲ୍ୟାଙ୍କନ', en: 'Assessment', icon: CheckSquare, color: 'from-cyan-500 to-blue-600' },
  book: { label: 'ପୁସ୍ତକ', en: 'Book', icon: BookMarked, color: 'from-amber-500 to-orange-600' },
  activity: { label: 'କାର୍ଯ୍ୟକଳାପ', en: 'Activity', icon: Sparkles, color: 'from-pink-500 to-rose-600' },
  pdf: { label: 'PDF', en: 'PDF', icon: FileText, color: 'from-red-500 to-rose-700' },
  image: { label: 'ଛବି', en: 'Image', icon: Image, color: 'from-lime-500 to-green-600' },
  video: { label: 'ଭିଡିଓ', en: 'Video', icon: Video, color: 'from-fuchsia-500 to-purple-700' },
  other: { label: 'ଅନ୍ୟ', en: 'Other', icon: Layers, color: 'from-gray-500 to-gray-700' },
};

export default function BrowsePage() {
  const { classSlug, subjectSlug, topicSlug } = useParams();
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [resources, setResources] = useState([]);
  const [ctx, setCtx] = useState({ class: null, subject: null, topic: null });
  const [loading, setLoading] = useState(true);

  // Level 1: Load classes
  useEffect(() => {
    if (!classSlug) {
      setLoading(true);
      axios.get(`${API}/discover/classes`).then(r => setClasses(r.data)).finally(() => setLoading(false));
    }
  }, [classSlug]);

  // Level 2: Load subjects for class
  useEffect(() => {
    if (classSlug && !subjectSlug) {
      setLoading(true);
      axios.get(`${API}/discover/subjects?class_slug=${classSlug}`).then(r => setSubjects(r.data)).finally(() => setLoading(false));
    }
  }, [classSlug, subjectSlug]);

  // Level 3: Load topics for class+subject
  useEffect(() => {
    if (classSlug && subjectSlug && !topicSlug) {
      setLoading(true);
      axios.get(`${API}/discover/topics?class_slug=${classSlug}&subject_slug=${subjectSlug}`).then(r => setTopics(r.data)).finally(() => setLoading(false));
    }
  }, [classSlug, subjectSlug, topicSlug]);

  // Level 4: Load topic + resources
  useEffect(() => {
    if (classSlug && subjectSlug && topicSlug) {
      setLoading(true);
      Promise.all([
        axios.get(`${API}/discover/topic?class_slug=${classSlug}&subject_slug=${subjectSlug}&topic_slug=${topicSlug}`),
        axios.get(`${API}/discover/topics?class_slug=${classSlug}&subject_slug=${subjectSlug}`),
      ]).then(([topicRes, topicsRes]) => {
        setCtx({ class: topicRes.data.class, subject: topicRes.data.subject, topic: topicRes.data.topic });
        setTopics(topicsRes.data);
        return axios.get(`${API}/discover/resources?topic_id=${topicRes.data.topic.id}`);
      }).then(r => setResources(r.data)).catch(() => {
        setCtx({ class: null, subject: null, topic: null });
      }).finally(() => setLoading(false));
    }
  }, [classSlug, subjectSlug, topicSlug]);

  const currentTopicIdx = ctx.topic ? topics.findIndex(t => t.id === ctx.topic.id) : -1;
  const prevTopic = currentTopicIdx > 0 ? topics[currentTopicIdx - 1] : null;
  const nextTopic = currentTopicIdx >= 0 && currentTopicIdx < topics.length - 1 ? topics[currentTopicIdx + 1] : null;

  // Group resources by type
  const byType = resources.reduce((acc, r) => {
    (acc[r.resource_type] = acc[r.resource_type] || []).push(r);
    return acc;
  }, {});

  return (
    <div className="px-4 pt-4 space-y-4 pb-8" data-testid="browse-page">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-[11px] text-gray-500 flex-wrap" data-testid="breadcrumb">
        <Link to="/" className="hover:text-emerald-600">Home</Link>
        <ChevronRight size={11} />
        <Link to="/teacher/browse" className="hover:text-emerald-600">Teacher</Link>
        {classSlug && (<>
          <ChevronRight size={11} />
          <Link to={`/teacher/browse/${classSlug}`} className="hover:text-emerald-600">{ctx.class?.name_odia || classSlug}</Link>
        </>)}
        {subjectSlug && (<>
          <ChevronRight size={11} />
          <Link to={`/teacher/browse/${classSlug}/${subjectSlug}`} className="hover:text-emerald-600">{ctx.subject?.name_odia || subjectSlug}</Link>
        </>)}
        {topicSlug && ctx.topic && (<>
          <ChevronRight size={11} />
          <span className="text-gray-800 font-semibold">{ctx.topic.name_odia}</span>
        </>)}
      </nav>

      {!classSlug && (
        <>
          <div className="rounded-3xl p-5 text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #059669 0%, #0891b2 60%, #7c3aed 100%)' }}>
            <GraduationCap size={26} />
            <h1 className="text-xl font-extrabold mt-2">📚 ଶ୍ରେଣୀ ବାଛନ୍ତୁ · Select Class</h1>
            <p className="text-xs text-white/90 mt-1">Balvatika to Class 10 · ସମସ୍ତ ବିଷୟ ଉପଲବ୍ଧ</p>
          </div>
          {loading ? <div className="text-center py-8 text-gray-500 text-sm">Loading...</div> :
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
              {classes.map(c => (
                <Link key={c.id} to={`/teacher/browse/${c.slug}`} data-testid={`class-${c.slug}`}
                  className="p-4 rounded-2xl border-2 border-gray-200 bg-white hover:border-emerald-400 hover:bg-emerald-50 text-center transition-all">
                  <div className="text-xl font-extrabold text-emerald-700">{c.name_odia}</div>
                  <div className="text-[10px] text-gray-500 mt-1">{c.name_en}</div>
                </Link>
              ))}
            </div>}
        </>
      )}

      {classSlug && !subjectSlug && (
        <>
          <div className="rounded-3xl p-5 text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #0891b2 0%, #059669 100%)' }}>
            <BookOpen size={22} />
            <h1 className="text-lg font-extrabold mt-1.5">📖 ବିଷୟ ବାଛନ୍ତୁ · Select Subject</h1>
            <p className="text-[11px] text-white/90">{classes.find(c => c.slug === classSlug)?.name_odia || classSlug}</p>
          </div>
          {loading ? <div className="text-center py-8 text-gray-500 text-sm">Loading...</div> :
            <div className="space-y-2">
              {subjects.length === 0 && <EmptyState msg="ଏହି ଶ୍ରେଣୀ ପାଇଁ ବିଷୟ ଉପଲବ୍ଧ ନାହିଁ" />}
              {subjects.map(s => (
                <Link key={s.id} to={`/teacher/browse/${classSlug}/${s.slug}`} data-testid={`subject-${s.slug}`}
                  className="w-full flex items-center gap-3 p-4 rounded-2xl border-2 border-gray-200 bg-white hover:border-emerald-400 hover:bg-emerald-50 transition-all">
                  <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-700 shrink-0"><BookOpen size={20}/></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm">{s.name_odia}</div>
                    <div className="text-[11px] text-gray-500">{s.name_en}</div>
                  </div>
                  <ChevronRight size={16} className="text-gray-400"/>
                </Link>
              ))}
            </div>}
        </>
      )}

      {classSlug && subjectSlug && !topicSlug && (
        <>
          <div className="rounded-3xl p-5 text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)' }}>
            <Layers size={22} />
            <h1 className="text-lg font-extrabold mt-1.5">🎯 ପ୍ରସଙ୍ଗ ବାଛନ୍ତୁ · Select Topic</h1>
          </div>
          {loading ? <div className="text-center py-8 text-gray-500 text-sm">Loading...</div> :
            <div className="space-y-2">
              {topics.length === 0 && <EmptyState msg="ଏହି ବିଷୟ ପାଇଁ ପ୍ରସଙ୍ଗ ଏଯାଏଁ ଯୋଡ଼ାଯାଇ ନାହିଁ" />}
              {topics.map(t => (
                <Link key={t.id} to={`/teacher/browse/${classSlug}/${subjectSlug}/${t.slug}`} data-testid={`topic-${t.slug}`}
                  className="w-full flex items-center gap-3 p-4 rounded-2xl border-2 border-gray-200 bg-white hover:border-purple-400 hover:bg-purple-50 transition-all">
                  <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold shrink-0">{t.chapter_number || '•'}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm">{t.name_odia}</div>
                    <div className="text-[11px] text-gray-500">{t.name_en}</div>
                  </div>
                  {t.status === 'coming_soon' && <span className="text-[9px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold">Coming Soon</span>}
                  <ChevronRight size={16} className="text-gray-400"/>
                </Link>
              ))}
            </div>}
        </>
      )}

      {classSlug && subjectSlug && topicSlug && ctx.topic && (
        <>
          <div className="rounded-3xl p-5 text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #059669 0%, #0891b2 60%, #7c3aed 100%)' }}>
            <div className="text-[10px] font-bold text-white/80 uppercase">{ctx.class?.name_odia} · {ctx.subject?.name_odia}</div>
            <h1 className="text-xl font-extrabold mt-1">{ctx.topic.name_odia}</h1>
            <p className="text-[11px] text-white/85">{ctx.topic.name_en}</p>
          </div>

          {/* Topic Prev/Next */}
          <div className="flex justify-between gap-2">
            {prevTopic ? (
              <button onClick={() => navigate(`/teacher/browse/${classSlug}/${subjectSlug}/${prevTopic.slug}`)} data-testid="prev-topic"
                className="flex-1 p-2 rounded-xl bg-white border-2 border-gray-200 hover:border-emerald-400 text-left flex items-center gap-2">
                <ArrowLeft size={14} className="text-gray-500 shrink-0"/>
                <div className="min-w-0"><div className="text-[9px] text-gray-500">ପୂର୍ବ ପ୍ରସଙ୍ଗ</div><div className="text-[11px] font-bold truncate">{prevTopic.name_odia}</div></div>
              </button>
            ) : <div className="flex-1"></div>}
            {nextTopic ? (
              <button onClick={() => navigate(`/teacher/browse/${classSlug}/${subjectSlug}/${nextTopic.slug}`)} data-testid="next-topic"
                className="flex-1 p-2 rounded-xl bg-white border-2 border-gray-200 hover:border-emerald-400 text-right flex items-center gap-2 justify-end">
                <div className="min-w-0"><div className="text-[9px] text-gray-500">ପରବର୍ତ୍ତୀ ପ୍ରସଙ୍ଗ</div><div className="text-[11px] font-bold truncate">{nextTopic.name_odia}</div></div>
                <ChevronRight size={14} className="text-gray-500 shrink-0"/>
              </button>
            ) : <div className="flex-1"></div>}
          </div>

          {/* Resources by type */}
          {resources.length === 0 && <EmptyState msg="ଏହି ପ୍ରସଙ୍ଗ ପାଇଁ ବର୍ତ୍ତମାନ କୌଣସି ବିଷୟବସ୍ତୁ ଉପଲବ୍ଧ ନାହିଁ।" />}
          {Object.entries(RESOURCE_ICONS).map(([type, meta]) => {
            const items = byType[type] || [];
            if (items.length === 0) return null;
            const Icon = meta.icon;
            return (
              <Card key={type} className="rounded-2xl p-3 border-gray-200" data-testid={`resource-group-${type}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${meta.color} flex items-center justify-center text-white`}><Icon size={14}/></div>
                  <div>
                    <div className="font-bold text-sm">{meta.label}</div>
                    <div className="text-[10px] text-gray-500">{meta.en} · {items.length} available</div>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {items.map(r => (
                    <Link key={r.id} to={`/teacher/resource/${r.id}`} data-testid={`resource-${r.id}`}
                      className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg hover:bg-emerald-50 border border-gray-200 hover:border-emerald-300">
                      <FileText size={14} className="text-emerald-600 shrink-0"/>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-gray-900 truncate">{r.title_odia || r.title}</div>
                        {r.description_odia && <div className="text-[10px] text-gray-500 truncate">{r.description_odia}</div>}
                      </div>
                      <ChevronRight size={12} className="text-gray-400"/>
                    </Link>
                  ))}
                </div>
              </Card>
            );
          })}
        </>
      )}
    </div>
  );
}

const EmptyState = ({ msg }) => (
  <Card className="rounded-2xl p-6 border-amber-200 bg-amber-50 text-center">
    <div className="text-3xl">📭</div>
    <div className="text-sm font-bold text-amber-800 mt-2">{msg}</div>
    <Link to="/teacher/browse" className="mt-3 inline-block text-xs font-bold text-emerald-700 underline">ଅନ୍ୟ ପ୍ରସଙ୍ଗ ଦେଖନ୍ତୁ</Link>
  </Card>
);
